/**
 * Assumptions the accounts would support, and the argument for each.
 *
 * The engine's discount rate is 12% for every company in every sector, and its terminal
 * growth is 2.5% — figures chosen to be harmless rather than right. A user who has no
 * view on either is stuck with them, and a valuation built on them is built on nothing.
 *
 * APPLIED ONE AT A TIME, BY HAND. Not "apply all", and not applied on arrival. Each
 * proposal is a row with its reasoning and its own button, so accepting one is accepting
 * that argument rather than accepting the set. The accounting engine has a reference to
 * check itself against — Mike's workbook — and the valuation engine has none; a proposed
 * rate written in silently would look exactly as settled as a reconciled figure.
 *
 * The reasoning is not decoration and is never collapsed behind a disclosure. It is the
 * only thing that distinguishes a proposal from a different arbitrary number.
 */

import { useState } from 'react';
import { WorkingIndicator } from '../../../components/WorkingIndicator';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import { useAuthStore } from '../../../store/auth';
import type { ValuationConfig } from '../../../types/financial-model';

interface Proposal {
  field: string;
  value: number | string;
  current: number | string | null;
  reasoning: string;
}

interface Assumptions {
  proposals: Proposal[];
  understanding: string;
  declined: string[];
}

/** How each field reads to a human, and whether it is a rate. */
const FIELDS: Record<string, { label: string; rate?: boolean; multiple?: boolean }> = {
  discountRate: { label: 'Discount rate', rate: true },
  terminalGrowthRate: { label: 'Terminal growth', rate: true },
  exitMultipleEVEBITDA: { label: 'Exit multiple (EV/EBITDA)', multiple: true },
  compEVRevenue: { label: 'Comparable EV / revenue', multiple: true },
  compEVEBITDA: { label: 'Comparable EV / EBITDA', multiple: true },
  compPE: { label: 'Comparable P / E', multiple: true },
  businessType: { label: 'What the business is' },
};

function show(field: string, value: number | string | null): string {
  if (value === null || value === undefined || value === '') return 'not set';
  const spec = FIELDS[field];
  if (typeof value !== 'number') return String(value).replace(/_/g, ' ');
  if (spec?.rate) return `${(value * 100).toFixed(1)}%`;
  if (spec?.multiple) return `${value.toFixed(1)}×`;
  return String(value);
}

export function ProposedAssumptions() {
  const model = useFinancialModelV2Store((s) => s.model);
  const updateSetting = useFinancialModelV2Store((s) => s.updateSetting);

  const [assumptions, setAssumptions] = useState<Assumptions | null>(null);
  const [isAsking, setIsAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<string[]>([]);

  if (!model) return null;

  const ask = async () => {
    setIsAsking(true);
    setError(null);
    setApplied([]);
    try {
      const token = useAuthStore.getState().token;
      const response = await fetch(
        `/api/v2/models/${model.id}/valuation-assumptions`,
        {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail.detail ?? `Nothing came back (${response.status})`);
      }
      setAssumptions(await response.json());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setIsAsking(false);
    }
  };

  const apply = (proposal: Proposal) => {
    const config = model.settings.valuationConfig;
    updateSetting('valuationConfig', {
      ...config,
      [proposal.field]: proposal.value,
    } as ValuationConfig);
    setApplied((current) => [...current, proposal.field]);
  };

  return (
    <section className="mt-8 max-w-4xl">
      <h3 className="text-sm font-semibold text-gray-900">Proposed assumptions</h3>
      <p className="mt-1 text-xs leading-relaxed text-gray-600">
        This model's discount rate and terminal growth are the engine's defaults — 12%
        and 2.5% for every company in every sector, not figures chosen for this one.
        These are suggestions from the accounts above, each with its reasoning, applied
        one at a time or not at all.
      </p>

      <button
        type="button"
        disabled={isAsking}
        onClick={() => void ask()}
        className="mt-3 rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
      >
        {isAsking ? 'Thinking…' : assumptions ? 'Ask again' : 'Propose assumptions'}
      </button>

      {isAsking && (
        <div className="mt-3 rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <WorkingIndicator label="Reading the statements" />
        </div>
      )}

      {error && (
        <div className="mt-3 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {assumptions && (
        <div className="mt-4 space-y-3">
          {assumptions.understanding && (
            <div className="rounded border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
              {/* Shown FIRST and on its own. If it has misread what the company is,
                  every rate below it is wrong for a reason the rates themselves will
                  not reveal. */}
              <span className="font-medium text-gray-900">What it took this to be: </span>
              {assumptions.understanding}
            </div>
          )}

          {assumptions.proposals.length === 0 && (
            <p className="text-sm text-gray-500">
              Nothing was proposed. The accounts did not support a view — which is an
              answer, and a better one than a number with no argument behind it.
            </p>
          )}

          {assumptions.proposals.map((proposal) => {
            const done = applied.includes(proposal.field);
            return (
              <div
                key={proposal.field}
                className="rounded-lg border border-gray-200 bg-white p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900">
                      {FIELDS[proposal.field]?.label ?? proposal.field}
                    </div>
                    <div className="mt-0.5 text-sm tabular-nums text-gray-600">
                      {show(proposal.field, proposal.current)}
                      <span aria-hidden="true" className="mx-2 text-gray-400">
                        →
                      </span>
                      <span className="font-semibold text-gray-900">
                        {show(proposal.field, proposal.value)}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={done}
                    onClick={() => apply(proposal)}
                    className="shrink-0 rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:border-green-200 disabled:bg-green-50 disabled:text-green-800 disabled:opacity-100"
                  >
                    {done ? 'Applied' : 'Apply this one'}
                  </button>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-gray-600">
                  {proposal.reasoning}
                </p>
              </div>
            );
          })}

          {assumptions.declined.length > 0 && (
            <div className="rounded border border-gray-200 bg-white px-4 py-3">
              <div className="text-xs font-medium text-gray-700">
                Left alone, and why
              </div>
              {/* Kept visible. "It had no view on the P/E because the company is
                  loss-making" is information about the company, not an apology. */}
              <ul className="mt-1.5 list-disc space-y-1 pl-4 text-xs text-gray-600">
                {assumptions.declined.map((entry) => (
                  <li key={entry}>{entry}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
